import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Maximize2, Minimize2, ArrowLeft, Trophy, Crown, Volume2, VolumeX } from "lucide-react";
import api from "@/lib/api";
import { useSiteLogo } from "@/lib/siteConfig";

const discLabel = { artistica: "Ginnastica Artistica", ritmica: "Ginnastica Ritmica" };
const medal = ["#D4AF37", "#C0C0C0", "#CD7F32"];

export default function LiveBoard() {
  const logoUrl = useSiteLogo();
  const [groups, setGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("all");
  const [discipline, setDiscipline] = useState("artistica");
  const [scope, setScope] = useState("general");
  const [loading, setLoading] = useState(true);
  const [fs, setFs] = useState(false);
  const [podium, setPodium] = useState(false);
  const [pedana, setPedana] = useState(null);

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const load = () => api.get("/live/pedana").then((r) => setPedana(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const fetchData = useCallback((showLoader) => {
    const params = { scope, discipline };
    if (eventId !== "all") params.event_id = eventId;
    if (showLoader) setLoading(true);
    api.get("/rankings", { params }).then((r) => setGroups(r.data)).finally(() => { if (showLoader) setLoading(false); });
  }, [scope, discipline, eventId]);

  useEffect(() => {
    fetchData(true);
    const t = setInterval(() => fetchData(false), 5000);
    return () => clearInterval(t);
  }, [fetchData]);

  const toggleFs = () => {
    if (!document.fullscreenElement) { document.documentElement.requestFullscreen?.(); setFs(true); }
    else { document.exitFullscreen?.(); setFs(false); }
  };

  const shown = useMemo(() => groups.filter((g) => g.discipline === discipline), [groups, discipline]);
  const selectedEvent = events.find((e) => e.id === eventId);

  return (
    <div className="min-h-screen bg-[#020617] text-foreground noise-bg">
      {/* Barra controlli */}
      <div className="sticky top-0 z-10 bg-[#020617]/90 backdrop-blur border-b border-white/10 px-6 py-3 flex flex-wrap items-center gap-3 print:hidden">
        <Link to="/app?section=classifica" data-testid="liveboard-back" className="text-muted-foreground hover:text-gold inline-flex items-center gap-1.5 text-sm"><ArrowLeft className="w-4 h-4" /> Esci</Link>
        <div className="h-5 w-px bg-white/10" />
        <div className="inline-flex rounded-lg border border-white/10 bg-[#0F172A] p-1">
          <button data-testid="liveboard-disc-artistica" onClick={() => setDiscipline("artistica")} className={`px-3 py-1 rounded text-sm font-medium ${discipline === "artistica" ? "bg-gold text-[#020617]" : "text-muted-foreground"}`}>Artistica</button>
          <button data-testid="liveboard-disc-ritmica" onClick={() => setDiscipline("ritmica")} className={`px-3 py-1 rounded text-sm font-medium ${discipline === "ritmica" ? "bg-gold text-[#020617]" : "text-muted-foreground"}`}>Ritmica</button>
        </div>
        <div className="inline-flex rounded-lg border border-white/10 bg-[#0F172A] p-1">
          <button data-testid="liveboard-scope-general" onClick={() => setScope("general")} className={`px-3 py-1 rounded text-sm font-medium ${scope === "general" ? "bg-gold text-[#020617]" : "text-muted-foreground"}`}>Generale</button>
          <button data-testid="liveboard-scope-apparatus" onClick={() => setScope("apparatus")} className={`px-3 py-1 rounded text-sm font-medium ${scope === "apparatus" ? "bg-gold text-[#020617]" : "text-muted-foreground"}`}>Per attrezzo</button>
        </div>
        <select data-testid="liveboard-event" value={eventId} onChange={(e) => setEventId(e.target.value)} className="bg-[#0F172A] border border-white/15 rounded-lg px-3 py-1.5 text-sm">
          <option value="all">Tutte le gare</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-3">
          <button data-testid="liveboard-podium-toggle" onClick={() => setPodium((v) => !v)} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium border transition-colors ${podium ? "bg-gold text-[#020617] border-gold" : "border-white/15 text-muted-foreground hover:text-gold"}`} title="Mostra il podio con premiazione">
            <Trophy className="w-4 h-4" /> Podio
          </button>
          <span className="inline-flex items-center gap-1.5 text-xs text-[#10B981]"><span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> LIVE</span>
          <button data-testid="liveboard-fullscreen" onClick={toggleFs} className="text-muted-foreground hover:text-gold" title="Schermo intero">{fs ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</button>
        </div>
      </div>

      {/* Intestazione */}
      <div className="text-center pt-8 pb-6 px-6">
        {logoUrl && <img src={logoUrl} alt="OPES" className="h-16 mx-auto mb-3 object-contain" />}
        <h1 className="font-display text-4xl md:text-6xl tracking-tight">Classifica <span className="text-gold">Live</span></h1>
        <p className="text-muted-foreground mt-2 text-lg md:text-xl">{discLabel[discipline]} · {scope === "general" ? "Classifica Generale" : "Per attrezzo"}{selectedEvent ? ` · ${selectedEvent.title}` : ""}</p>
      </div>

      {/* Pedana live: atleta in pedana ora + il prossimo */}
      {pedana?.current && (
        <div data-testid="liveboard-pedana" className="px-6 pb-4 max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border-2 border-gold bg-gold/10 p-5 md:p-6">
              <p className="text-xs md:text-sm uppercase tracking-widest text-gold mb-1 flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-gold animate-pulse" /> In pedana ora</p>
              <p data-testid="liveboard-pedana-current" className="font-display text-3xl md:text-5xl leading-tight truncate">{pedana.current.name}</p>
              <p className="text-muted-foreground text-base md:text-xl mt-1 truncate">{pedana.current.sub}{pedana.current.club ? ` · ${pedana.current.club}` : ""}</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-[#0F172A]/60 p-5 md:p-6">
              <p className="text-xs md:text-sm uppercase tracking-widest text-muted-foreground mb-1">Il prossimo</p>
              <p data-testid="liveboard-pedana-next" className="font-display text-2xl md:text-4xl leading-tight truncate">{pedana.next ? pedana.next.name : "—"}</p>
              <p className="text-muted-foreground text-base md:text-xl mt-1 truncate">{pedana.next ? pedana.next.sub + (pedana.next.club ? ` · ${pedana.next.club}` : "") : "Fine lista"}</p>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 pb-16 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>
        ) : shown.length === 0 ? (
          <div className="text-center py-24" data-testid="liveboard-empty">
            <Trophy className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-xl text-muted-foreground">In attesa dei risultati…</p>
            <p className="text-sm text-muted-foreground/70 mt-1">La classifica appare quando tutti i giudici hanno confermato i voti.</p>
          </div>
        ) : podium ? (
          <PodiumView groups={shown} />
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="liveboard-grid">
            {shown.map((g, gi) => (
              <div key={gi} className="rounded-2xl border border-white/10 bg-[#0F172A]/80 overflow-hidden">
                <div className="px-5 py-3 bg-gold/10 border-b border-gold/20">
                  <h2 className="font-display text-xl md:text-2xl">{g.category}{g.apparatus && g.apparatus !== "—" ? <span className="text-gold capitalize"> · {g.apparatus}</span> : ""}</h2>
                </div>
                <div className="divide-y divide-white/5">
                  {g.athletes.map((a) => (
                    <div key={a.athlete_id} data-testid={`liveboard-entry-${a.athlete_id}`} className="flex items-center gap-4 px-5 py-3">
                      <span className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-display text-lg font-bold" style={a.rank <= 3 ? { background: medal[a.rank - 1], color: "#020617" } : { background: "rgba(255,255,255,0.06)", color: "#94a3b8" }}>{a.rank}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-lg md:text-xl truncate">{a.name}</p>
                        {a.club && <p className="text-sm text-muted-foreground truncate">{a.club}</p>}
                      </div>
                      <span className="font-display text-2xl md:text-3xl text-gold tabular-nums">{Number(a.total).toFixed(3)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const PODIUM_H = { 1: 210, 2: 150, 3: 110 };
const POS_LABEL = { 1: "1°", 2: "2°", 3: "3°" };

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 70 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.8,
    dur: 2.4 + Math.random() * 2,
    color: ["#D4AF37", "#FDE047", "#C0C0C0", "#CD7F32", "#10B981", "#38BDF8", "#F472B6"][i % 7],
    size: 6 + Math.random() * 9,
    rot: Math.random() * 360,
    drift: (Math.random() - 0.5) * 120,
  })), []);
  return (
    <div className="pointer-events-none fixed inset-0 z-20 overflow-hidden" aria-hidden data-testid="podium-confetti">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ top: "-8%", opacity: 0, rotate: 0, x: 0 }}
          animate={{ top: "108%", opacity: [0, 1, 1, 0.7], rotate: p.rot + 540, x: p.drift }}
          transition={{ duration: p.dur, delay: p.delay, ease: "easeIn" }}
          style={{ position: "absolute", left: `${p.left}%`, width: p.size, height: p.size * 0.5, background: p.color, borderRadius: 2 }}
        />
      ))}
    </div>
  );
}

function PodiumBlock({ group }) {
  const top3 = group.athletes.slice(0, 3);
  const cols = [
    { a: top3[1], pos: 2 },
    { a: top3[0], pos: 1 },
    { a: top3[2], pos: 3 },
  ].filter((c) => c.a);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F172A]/80 p-6 md:p-8" data-testid="podium-block">
      <h2 className="font-display text-xl md:text-3xl text-center mb-10">{group.category}{group.apparatus && group.apparatus !== "—" ? <span className="text-gold capitalize"> · {group.apparatus}</span> : ""}</h2>
      <div className="flex items-end justify-center gap-3 md:gap-8">
        {cols.map((c, i) => {
          const color = medal[c.pos - 1];
          return (
            <div key={c.a.athlete_id} data-testid={`podium-place-${c.pos}`} className="flex flex-col items-center w-24 md:w-44">
              <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.6 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.55 + i * 0.4, type: "spring", stiffness: 200, damping: 12 }}
                className="flex flex-col items-center text-center mb-3"
              >
                {c.pos === 1 && (
                  <motion.div initial={{ rotate: -20, scale: 0 }} animate={{ rotate: 0, scale: 1 }} transition={{ delay: 1.1, type: "spring", stiffness: 260, damping: 10 }}>
                    <Crown className="w-8 h-8 md:w-10 md:h-10 mb-1" style={{ color }} />
                  </motion.div>
                )}
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center font-display text-xl md:text-2xl font-bold shadow-lg" style={{ background: color, color: "#020617" }}>{c.pos}</div>
                <p className="font-semibold text-sm md:text-lg mt-2 leading-tight">{c.a.name}</p>
                {c.a.club && <p className="text-[11px] md:text-xs text-muted-foreground truncate max-w-full">{c.a.club}</p>}
                <p className="font-display text-lg md:text-2xl text-gold tabular-nums mt-1">{Number(c.a.total).toFixed(3)}</p>
              </motion.div>
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: PODIUM_H[c.pos] }}
                transition={{ delay: 0.25 + i * 0.4, type: "spring", stiffness: 120, damping: 17 }}
                className="w-full rounded-t-lg border-t-2 overflow-hidden"
                style={{ background: `linear-gradient(180deg, ${color}40, ${color}0a)`, borderColor: color }}
              >
                <div className="text-center pt-3 font-display text-3xl md:text-5xl font-bold" style={{ color }}>{POS_LABEL[c.pos]}</div>
              </motion.div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

let _fanfareCtx = null;
function playFanfare() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    _fanfareCtx = _fanfareCtx || new AC();
    const ctx = _fanfareCtx;
    if (ctx.state === "suspended") ctx.resume();
    const t0 = ctx.currentTime + 0.02;
    const tone = (f, start, dur, peak) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(peak, start + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + dur + 0.05);
    };
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(f, t0 + i * 0.12, 0.45, 0.25));
    const ct = t0 + 0.5;
    [523.25, 659.25, 783.99, 1046.5].forEach((f) => tone(f, ct, 1.0, 0.2));
  } catch (e) { /* audio non disponibile */ }
}

const podKey = (g) => `${g.category}||${g.apparatus || ""}`;
const podLabel = (g) => `${g.category}${g.apparatus && g.apparatus !== "—" ? " · " + g.apparatus : ""}`;

function PodiumView({ groups }) {
  const [sel, setSel] = useState(groups[0] ? podKey(groups[0]) : "");
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => {
    if (groups.length && !groups.some((g) => podKey(g) === sel)) setSel(podKey(groups[0]));
  }, [groups, sel]);
  const current = groups.find((g) => podKey(g) === sel) || groups[0];
  useEffect(() => {
    if (!mutedRef.current) playFanfare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);
  if (!current) return null;
  return (
    <div className="relative" data-testid="liveboard-podium-view">
      <Confetti key={sel} />
      <div className="flex items-center justify-center gap-3 mb-8 flex-wrap">
        {groups.length > 1 && (
          <select data-testid="podium-category-select" value={sel} onChange={(e) => setSel(e.target.value)} className="bg-[#0F172A] border border-white/15 rounded-lg px-4 py-2 text-base capitalize">
            {groups.map((g) => <option key={podKey(g)} value={podKey(g)} className="capitalize">{podLabel(g)}</option>)}
          </select>
        )}
        <button data-testid="podium-mute-toggle" onClick={() => setMuted((m) => !m)} title={muted ? "Attiva suono premiazione" : "Disattiva suono premiazione"} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-sm text-muted-foreground hover:text-gold transition-colors">
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
      <div className="max-w-3xl mx-auto"><PodiumBlock group={current} /></div>
    </div>
  );
}
